import { createContext, useContext, useState, useEffect, useCallback } from "react";
import API, { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await API.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await API.post("/auth/login", { email, password });
    if (data.access_token) localStorage.setItem("access_token", data.access_token);
    if (data.refresh_token) localStorage.setItem("refresh_token", data.refresh_token);
    setUser(data);
    return data;
  };

  const adminLogin = async (email, password) => {
    const { data } = await API.post("/auth/admin-login", { email, password });
    if (data.access_token) localStorage.setItem("access_token", data.access_token);
    if (data.refresh_token) localStorage.setItem("refresh_token", data.refresh_token);
    setUser(data);
    return data;
  };

  const register = async (email, password, name, role, class_code) => {
    const { data } = await API.post("/auth/register", { email, password, name, role, class_code });
    if (data.access_token) localStorage.setItem("access_token", data.access_token);
    if (data.refresh_token) localStorage.setItem("refresh_token", data.refresh_token);
    setUser(data);
    return data;
  };

  const processGoogleSession = async (sessionId) => {
    const { data } = await API.post("/auth/google/callback", { session_id: sessionId });
    if (data.access_token) localStorage.setItem("access_token", data.access_token);
    if (data.refresh_token) localStorage.setItem("refresh_token", data.refresh_token);
    if (data.session_token) localStorage.setItem("session_token", data.session_token);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await API.post("/auth/logout");
    } catch { /* ignore */ }
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("session_token");
    setUser(null);
  };

  const refreshUser = async () => {
    try {
      const { data } = await API.get("/auth/me");
      setUser(data);
    } catch { /* ignore */ }
  };

  const updateUserCoins = (coins) => {
    setUser((prev) => prev ? { ...prev, coins } : prev);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, adminLogin, register, logout, refreshUser, processGoogleSession, formatApiError, updateUserCoins }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
